import { PayPalButtons as BaseButtons } from "@paypal/react-paypal-js"
import { type FC } from "react"

import { type PurchaseableType } from "@forento/shared/models/product"
import { exhaustiveGuard } from "@forento/shared/utilities/switch"

import { useAlert } from "~/contexts/AlertContext"
import { usePlatform } from "~/contexts/PlatformContext"
import { usePurchaseSuccessModal } from "~/contexts/PurchaseSuccessModalContext"
import { useTranslation } from "~/translations"
import trpc from "~/utilities/trpc"

type Props = {
	item: { id: number; type: PurchaseableType; price: { amount: number; currency: string } }
	discountCode: string | null
	onPurchase(args: { paypalOrder: { userStatus: "existing" | "assigned" | "created"; email: string } }): Promise<void>
}
const PayPalButtons: FC<Props> = ({ item, discountCode, onPurchase }) => {
	const platform = usePlatform().platform
	const t = useTranslation()
	const alert = useAlert()
	const purchaseSuccessModal = usePurchaseSuccessModal()

	if (platform.paymentGateway?.type !== "paypal") return null

	return (
		<BaseButtons
			style={{ layout: "horizontal", label: "buynow", tagline: false }}
			createOrder={async () => {
				const order = await trpc.payment.paypal.createOrder.mutate({ item, discountCode })
				if (order.status === "payeeAccountRestricted") {
					throw new Error("Payee account restricted")
				}
				return order.id
			}}
			onApprove={async (data, actions) => {
				const result = await trpc.payment.paypal.captureOrderByPayPalOrderId.mutate(data.orderID)
				switch (result.status) {
					case "instrumentDeclined": {
						actions.restart()
						break
					}
					case "payeeAccountRestricted": {
						await alert.show(
							"Payment failed",
							"The payee's account is restricted and cannot receive payments. Please contact support.",
						)
						break
					}
					case "invalidDiscountCode": {
						await alert.show("Invalid discount code", "The discount code is invalid.")
						break
					}
					case "success": {
						await onPurchase({ paypalOrder: { userStatus: result.userStatus, email: result.email } })
						purchaseSuccessModal.show()
						break
					}
					default:
						exhaustiveGuard(result)
				}
			}}
			onError={error => {
				if (error instanceof Error && error.message === "Payee account restricted") {
					alert.show(
						"Payment failed",
						"The payee's account is restricted and cannot receive payments. Please contact support.",
					)
					return
				}

				console.error("PayPal payment failed!", error)
				alert.show(t("general.error"), t("general.payPalPaymentFailed"))
			}}
		/>
	)
}

export default PayPalButtons
